import { supabase, type BookingWithDetails } from "../supabase";

export class BookingService {
  static async createBooking(
    userId: string,
    slotIds: string[],
    teamMembers: Array<{ name: string; phone_number?: string }>
  ): Promise<string | null> {
    try {
      // First, verify that the user exists in the user table
      const { data: userData, error: userError } = await supabase
        .from("user")
        .select("id")
        .eq("id", userId)
        .single();

      if (userError || !userData) {
        console.error("User not found in database:", userId, userError);
        throw new Error("User account not found. Please contact support.");
      }

      const { data: slotsData, error: slotsError } = await supabase
        .from("slot")
        .select("id, date")
        .in("id", slotIds);

      if (slotsError || !slotsData || slotsData.length === 0) {
        console.error("Slots not found");
        return null;
      }

      for (const slot of slotsData) {
        const hasBooking = await BookingService.hasBookingForDate(
          userId,
          slot.date
        );
        if (hasBooking) {
          console.error(`User already has booking on date ${slot.date}`);
          return null;
        }
      }

      const bookingsToInsert = slotIds.map((slotId) => ({
        user_id: userId,
        slot_id: slotId,
        status: "pending" as const,
        team_members: teamMembers,
      }));

      const { data, error } = await supabase
        .from("bookings")
        .insert(bookingsToInsert)
        .select();

      if (error) {
        console.error("Error creating booking:", error);
        // Check if it's a foreign key constraint error
        if (error.code === '23503') {
          if (error.message.includes('user_id')) {
            throw new Error("User account not found. Please log out and log in again.");
          } else if (error.message.includes('slot_id')) {
            throw new Error("Slot not found. Please refresh and try again.");
          }
        }
        return null;
      }

      return data[0]?.id || null;
    } catch (error) {
      console.error("Error in createBooking:", error);
      // Re-throw if it's a user-friendly error message
      if (error instanceof Error && error.message.includes("User account")) {
        throw error;
      }
      return null;
    }
  }

  static async getUserBookings(userId: string): Promise<BookingWithDetails[]> {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          *,
          user:user_id(*),
          slot:slot_id(*)
        `
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error getting user bookings:", error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error("Error in getUserBookings:", error);
      return [];
    }
  }

  static async getAllBookings(): Promise<BookingWithDetails[]> {
    try {
      // Fetch all bookings with user and slot details
      // Supabase has a default limit, so we need to fetch in batches
      let allBookings: any[] = [];
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("bookings")
          .select(
            `
            *,
            user:user_id(*),
            slot:slot_id(*)
          `
          )
          .order("created_at", { ascending: false })
          .range(from, from + batchSize - 1);

        if (error) {
          console.error("Error getting all bookings:", error);
          console.error("Error details:", JSON.stringify(error, null, 2));
          // If there's an error, try to return what we have so far
          break;
        }

        if (!data || data.length === 0) {
          hasMore = false;
          break;
        }

        allBookings = allBookings.concat(data);
        console.log(`Fetched ${data.length} bookings (total so far: ${allBookings.length})`);

        // Check if we got all bookings (if less than batchSize, we're done)
        if (data.length < batchSize) {
          hasMore = false;
        } else {
          from += batchSize;
        }
      }

      if (allBookings.length === 0) {
        console.log("No bookings found in database");
        return [];
      }

      console.log(`Found ${allBookings.length} total bookings in database`);

      // Log any bookings with missing related data for debugging
      const bookingsWithMissingData: any[] = [];
      const bookingsByDate: Record<string, number> = {};
      
      allBookings.forEach((booking: any) => {
        // Count bookings by date
        const slotDate = booking.slot?.date || 'unknown';
        bookingsByDate[slotDate] = (bookingsByDate[slotDate] || 0) + 1;

        if (!booking.user || !booking.slot) {
          bookingsWithMissingData.push({
            bookingId: booking.id,
            hasUser: !!booking.user,
            hasSlot: !!booking.slot,
            userId: booking.user_id,
            slotId: booking.slot_id,
            status: booking.status,
            createdAt: booking.created_at,
            slotDate: booking.slot?.date
          });
          console.warn(`Booking ${booking.id} has missing related data:`, {
            hasUser: !!booking.user,
            hasSlot: !!booking.slot,
            userId: booking.user_id,
            slotId: booking.slot_id,
            status: booking.status,
            slotDate: booking.slot?.date
          });
        }
      });

      console.log(`Bookings by date:`, bookingsByDate);
      if (bookingsByDate['2025-11-09']) {
        console.log(`Bookings for 2025-11-09: ${bookingsByDate['2025-11-09']}`);
      }

      if (bookingsWithMissingData.length > 0) {
        console.warn(`${bookingsWithMissingData.length} bookings have missing user or slot data`);
        console.warn("Sample bookings with missing data:", bookingsWithMissingData.slice(0, 5));
      }

      // Return all bookings that have both user and slot data
      // This ensures we can display them properly in the admin dashboard
      const validBookings = allBookings.filter((booking: any) => {
        return booking.user && booking.slot;
      }) as BookingWithDetails[];

      // Count valid bookings by date
      const validBookingsByDate: Record<string, number> = {};
      validBookings.forEach((booking: any) => {
        const slotDate = booking.slot?.date || 'unknown';
        validBookingsByDate[slotDate] = (validBookingsByDate[slotDate] || 0) + 1;
      });
      console.log(`Valid bookings by date:`, validBookingsByDate);
      if (validBookingsByDate['2025-11-09']) {
        console.log(`Valid bookings for 2025-11-09: ${validBookingsByDate['2025-11-09']}`);
      }

      console.log(`Returning ${validBookings.length} valid bookings (with user and slot data)`);
      
      if (allBookings.length !== validBookings.length) {
        console.warn(`Filtered out ${allBookings.length - validBookings.length} bookings due to missing user or slot data`);
      }

      return validBookings;
    } catch (error) {
      console.error("Error in getAllBookings:", error);
      return [];
    }
  }

  static async updateBookingStatus(
    bookingId: string,
    status: "approved" | "rejected",
    rejectionReason?: string
  ): Promise<boolean> {
    try {
      type BookingUpdate = {
        status: "approved" | "rejected" | "pending";
        rejection_reason?: string | null;
      };

      const updateObj: BookingUpdate = { status };
      if (status === "rejected" && rejectionReason) {
        updateObj.rejection_reason = rejectionReason;
      } else if (status === "approved") {
        updateObj.rejection_reason = null;
      }

      const { error } = await supabase
        .from("bookings")
        .update(updateObj)
        .eq("id", bookingId);

      if (error) {
        console.error("Error updating booking status:", error);
        return false;
      }

      if (status === "approved") {
        const { data: booking } = await supabase
          .from("bookings")
          .select("slot_id")
          .eq("id", bookingId)
          .single();
        if (booking) {
          await supabase
            .from("slot")
            .update({ status: "approved" })
            .eq("id", booking.slot_id);
        }
      }

      if (status === "rejected") {
        const { data: booking } = await supabase
          .from("bookings")
          .select("slot_id")
          .eq("id", bookingId)
          .single();
        if (booking) {
          // Clear the slot completely when rejecting
          await supabase
            .from("slot")
            .update({
              status: "pending",
              booked_by_user_id: null,
              booking_id: null,
            })
            .eq("id", booking.slot_id);
        }
      }

      return true;
    } catch (error) {
      console.error("Error in updateBookingStatus:", error);
      return false;
    }
  }

  static async cancelBooking(
    bookingId: string,
    userId: string
  ): Promise<boolean> {
    try {
      const { data: booking, error: fetchError } = await supabase
        .from("bookings")
        .select("*, slot:slot_id(*)")
        .eq("id", bookingId)
        .eq("user_id", userId)
        .single();

      if (fetchError || !booking) {
        console.error("Booking not found or unauthorized");
        return false;
      }

      const bookingDate = new Date(booking.slot.date);
      const now = new Date();
      const hoursUntilBooking =
        (bookingDate.getTime() - now.getTime()) / (1000 * 60 * 60);

      if (hoursUntilBooking <= 24) {
        console.error("Cannot cancel booking less than 24 hours before");
        return false;
      }

      // Update booking status to cancelled (not rejected)
      const { error: updateError } = await supabase
        .from("bookings")
        .update({ status: "cancelled" })
        .eq("id", bookingId);

      if (updateError) {
        console.error("Error cancelling booking:", updateError);
        return false;
      }

      // Clear the slot completely
      await supabase
        .from("slot")
        .update({
          status: "pending",
          booked_by_user_id: null,
          booking_id: null,
        })
        .eq("id", booking.slot_id);

      return true;
    } catch (error) {
      console.error("Error in cancelBooking:", error);
      return false;
    }
  }

  static async hasBookingForDate(
    userId: string,
    date: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select("id, slot_id, slot:slot_id(date)")
        .eq("user_id", userId)
        .not("status", "in", '("rejected","cancelled")');

      if (error) {
        console.error("Error in hasBookingForDate:", error);
        return false;
      }

      const hasBookingOnDate = data?.some((booking: any) => {
        return booking.slot?.date === date;
      });

      return hasBookingOnDate || false;
    } catch (error) {
      console.error("Error in hasBookingForDate:", error);
      return false;
    }
  }

  static async adminCancelBooking(bookingId: string): Promise<boolean> {
    try {
      const { data: booking, error: fetchError } = await supabase
        .from("bookings")
        .select("*, slot:slot_id(*)")
        .eq("id", bookingId)
        .single();

      if (fetchError || !booking) {
        console.error("Booking not found");
        return false;
      }

      // Update booking status to cancelled
      const { error: updateError } = await supabase
        .from("bookings")
        .update({ status: "cancelled" })
        .eq("id", bookingId);

      if (updateError) {
        console.error("Error cancelling booking:", updateError);
        return false;
      }

      // Clear the slot completely
      await supabase
        .from("slot")
        .update({
          status: "pending",
          booked_by_user_id: null,
          booking_id: null,
        })
        .eq("id", booking.slot_id);

      return true;
    } catch (error) {
      console.error("Error in adminCancelBooking:", error);
      return false;
    }
  }

  static async hasActiveBooking(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select("id, slot_id, slot:slot_id(date)")
        .eq("user_id", userId)
        .not("status", "in", '("rejected","cancelled")')
        .gte("slot.date", new Date().toISOString().split("T")[0]);

      if (error) {
        console.error("Error in hasActiveBooking:", error);
        return false;
      }

      return data && data.length > 0;
    } catch (error) {
      console.error("Error in hasActiveBooking:", error);
      return false;
    }
  }

  // New method to delete a booking completely (if needed)
  static async deleteBooking(bookingId: string): Promise<boolean> {
    try {
      // First get the slot_id before deleting
      const { data: booking, error: fetchError } = await supabase
        .from("bookings")
        .select("slot_id")
        .eq("id", bookingId)
        .single();

      if (fetchError || !booking) {
        console.error("Booking not found");
        return false;
      }

      // Clear the slot first
      await supabase
        .from("slot")
        .update({
          status: "pending",
          booked_by_user_id: null,
          booking_id: null,
        })
        .eq("id", booking.slot_id);

      // Then delete the booking
      const { error: deleteError } = await supabase
        .from("bookings")
        .delete()
        .eq("id", bookingId);

      if (deleteError) {
        console.error("Error deleting booking:", deleteError);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in deleteBooking:", error);
      return false;
    }
  }
}
